import type { DlqMessageSummary, JobLogEntry, RunbookMapping, RunbookSearchResult } from '@dev-console/types';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  generateRunbookTemplate,
  lookupRunbook,
  saveRunbookMapping,
  searchRunbook,
  submitRunbookFeedback,
} from '@/config/api-helpers';
import { useRunbookStream } from '@/hooks/useRunbookStream';

type RunbookPhase = 'lookup' | 'searching' | 'pick-result' | 'summarizing' | 'feedback' | 'no-results' | 'error';

interface UseRunbookReturn {
  phase: RunbookPhase;
  mapping: RunbookMapping | null;
  searchResults: RunbookSearchResult[];
  streamContent: string;
  isStreaming: boolean;
  error: string;
  pickResult: (result: RunbookSearchResult) => void;
  pasteUrl: (url: string) => void;
  generateTemplate: () => Promise<string>;
  thumbsUp: () => void;
  thumbsDown: () => void;
  retry: () => void;
}

export function useRunbook(
  canonicalJob: string,
  dlqMessage?: DlqMessageSummary,
  logEntries?: JobLogEntry[]
): UseRunbookReturn {
  const [phase, setPhase] = useState<RunbookPhase>('lookup');
  const [mapping, setMapping] = useState<RunbookMapping | null>(null);
  const [searchResults, setSearchResults] = useState<RunbookSearchResult[]>([]);
  const [error, setError] = useState('');
  const {
    content: streamContent,
    isStreaming,
    error: streamError,
    startStream,
    reset: resetStream,
  } = useRunbookStream();

  // Use refs for values that change with props but shouldn't trigger re-fetches
  const startStreamRef = useRef(startStream);
  startStreamRef.current = startStream;
  const dlqMessageRef = useRef(dlqMessage);
  dlqMessageRef.current = dlqMessage;
  const logEntriesRef = useRef(logEntries);
  logEntriesRef.current = logEntries;

  const startSummarize = useCallback(
    (m: RunbookMapping) => {
      if (!m.notionPageId) {
        setError('Could not determine Notion page ID from the saved URL. Please re-link the runbook.');
        setPhase('error');
        return;
      }
      setPhase('summarizing');
      startStreamRef.current({
        canonicalJob,
        notionPageId: m.notionPageId,
        dlqMessage: dlqMessageRef.current,
        logEntries: logEntriesRef.current,
      });
    },
    [canonicalJob]
  );

  const doSearch = useCallback(async () => {
    setPhase('searching');
    const searchData = await searchRunbook(canonicalJob);
    if (searchData.results.length > 0) {
      setSearchResults(searchData.results);
      setPhase('pick-result');
    } else {
      setPhase('no-results');
    }
  }, [canonicalJob]);

  const doLookup = useCallback(async () => {
    setPhase('lookup');
    setError('');
    try {
      const data = await lookupRunbook(canonicalJob);
      if (data.mapping) {
        setMapping(data.mapping);
        startSummarize(data.mapping);
      } else {
        await doSearch();
      }
    } catch (err) {
      setError((err as Error).message);
      setPhase('error');
    }
  }, [canonicalJob, startSummarize, doSearch]);

  useEffect(() => {
    doLookup();
  }, [doLookup]);

  useEffect(() => {
    if (!isStreaming && streamContent && phase === 'summarizing') {
      setPhase('feedback');
    }
  }, [isStreaming, streamContent, phase]);

  useEffect(() => {
    if (streamError) {
      setError(streamError);
      setPhase('error');
    }
  }, [streamError]);

  const pickResult = useCallback(
    async (result: RunbookSearchResult) => {
      try {
        const data = await saveRunbookMapping({
          canonicalJob,
          notionUrl: result.url,
          notionPageId: result.pageId,
        });
        setMapping(data.mapping);
        startSummarize(data.mapping);
      } catch (err) {
        setError((err as Error).message);
        setPhase('error');
      }
    },
    [canonicalJob, startSummarize]
  );

  const pasteUrl = useCallback(
    async (url: string) => {
      try {
        const data = await saveRunbookMapping({ canonicalJob, notionUrl: url });
        setMapping(data.mapping);
        startSummarize(data.mapping);
      } catch (err) {
        setError((err as Error).message);
        setPhase('error');
      }
    },
    [canonicalJob, startSummarize]
  );

  const generateTemplate = useCallback(async (): Promise<string> => {
    const data = await generateRunbookTemplate({ canonicalJob });
    return data.template;
  }, [canonicalJob]);

  const thumbsUp = useCallback(async () => {
    try {
      await submitRunbookFeedback({ canonicalJob, vote: 'up' });
    } catch (err) {
      setError((err as Error).message);
      setPhase('error');
    }
  }, [canonicalJob]);

  const thumbsDown = useCallback(async () => {
    try {
      await submitRunbookFeedback({ canonicalJob, vote: 'down' });
      setMapping(null);
      setSearchResults([]);
      resetStream();
      await doSearch();
    } catch (err) {
      setError((err as Error).message);
      setPhase('error');
    }
  }, [canonicalJob, resetStream, doSearch]);

  const retry = useCallback(() => {
    resetStream();
    doLookup();
  }, [resetStream, doLookup]);

  return {
    phase,
    mapping,
    searchResults,
    streamContent,
    isStreaming,
    error,
    pickResult,
    pasteUrl,
    generateTemplate,
    thumbsUp,
    thumbsDown,
    retry,
  };
}
